// Discount preset resolver — mirror of DiscountPreset::resolveLines on the
// server. Keep both implementations aligned: FE drives the preset banner and
// the auto-filled discount amount; BE re-validates on submit.
//
// Three tier scopes, evaluated as three ring-fenced pools. A cart line
// belongs to exactly one pool, and pools never cross-subsidise.
//
//   - GROUP (group_key set): the product belongs to a category that shares
//     one ladder — e.g. Vet Pharma's "farm" products. Every line in the
//     group POOLS its quantity, and the summed qty is matched against the
//     group ladder. One Susmend Farm + one Avian Pro Farm = 2 farm bottles,
//     which clears the group's "2+" rung.
//   - PRODUCT (item_id set, no group_key): a chart that stands alone.
//     Resolved per line against its own tiers only. If the line's qty
//     reaches none of them the line earns nothing — it does NOT fall back to
//     the org-wide pool, and its units never count toward the org-wide
//     threshold.
//   - ORG-WIDE (item_id and group_key both null): every product with no
//     chart of its own. Those lines pool their quantity together, so a mixed
//     cart of 13 + 2 bottles still clears a "15 bottles" rung.
//
// `lines` shape: [{ item_id, quantity, subtotal }, ...]

function bestTierFor (tiers, qty) {
  const eligible = tiers.filter((t) => qty >= Number(t.min_quantity || 0))
  if (!eligible.length) return null
  return eligible.slice().sort((a, b) => Number(b.min_quantity) - Number(a.min_quantity))[0]
}

function computeAmount (tier, qty, subtotal) {
  const value = Number(tier.value || 0)
  if (tier.mode === 'flat_per_unit') return Number((qty * value).toFixed(2))
  return Number(((subtotal * value) / 100).toFixed(2))
}

// A pooled ladder is assembled from many rows — a group's rungs come from
// every member product, so eleven farm products contribute eleven rows at
// "min qty 2". They're meant to be identical, but if a member is edited out
// of step the ladder must still resolve the same way on the client and the
// server. Collapse rows sharing a min_quantity to the most conservative one
// (lowest value, percentage before flat) so the result never depends on row
// order — an under-discount is recoverable, an over-discount is not.
function normalizeLadder (tiers) {
  const byQty = {}
  tiers.forEach((t) => {
    const qty = Number(t.min_quantity || 0)
    const held = byQty[qty]
    if (!held) { byQty[qty] = t; return }
    const a = [Number(t.value || 0), String(t.mode || '')]
    const b = [Number(held.value || 0), String(held.mode || '')]
    if (a[0] < b[0] || (a[0] === b[0] && a[1] < b[1])) byQty[qty] = t
  })
  return Object.keys(byQty).map((k) => byQty[k])
}

// Single resolver behind every export below, so the banner total, the
// per-line breakdown and the server always tell the same story.
// Returns one entry per input line, in input order:
// [{ item_id, quantity, subtotal, scope, tier, amount, poolKey }]
function resolveLines (tiers, lines) {
  const list = Array.isArray(lines) ? lines : []
  const active = Array.isArray(tiers) ? tiers.filter((t) => t && Boolean(t.active)) : []

  const orgWideTiers = []
  const groupTiers = {}   // group_key => tier rows
  const productTiers = {} // item_id   => tier rows
  const itemGroup = {}    // item_id   => group_key

  active.forEach((t) => {
    const itemId = Number(t.item_id || 0)
    const groupKey = t.group_key ? String(t.group_key) : null
    if (groupKey) {
      if (!groupTiers[groupKey]) groupTiers[groupKey] = []
      groupTiers[groupKey].push(t)
      if (itemId) itemGroup[itemId] = groupKey
    } else if (itemId) {
      if (!productTiers[itemId]) productTiers[itemId] = []
      productTiers[itemId].push(t)
    } else {
      orgWideTiers.push(t)
    }
  })

  const poolQty = {}
  const poolLines = {}
  const poolTiers = {}

  const resolved = list.map((line, i) => {
    const itemId = Number(line.item_id || 0)
    const qty = Number(line.quantity || 0)
    const subtotal = Number(line.subtotal || 0)
    const entry = { item_id: line.item_id, quantity: qty, subtotal, scope: null, tier: null, amount: 0, poolKey: null }
    if (!qty || !active.length) return entry

    // Group member → pool with the rest of its category.
    if (itemId && itemGroup[itemId]) {
      const key = `group:${itemGroup[itemId]}`
      poolTiers[key] = groupTiers[itemGroup[itemId]]
      poolQty[key] = (poolQty[key] || 0) + qty
      if (!poolLines[key]) poolLines[key] = []
      poolLines[key].push(i)
      entry.scope = 'group'
      entry.poolKey = key
      return entry
    }

    // Standalone product chart → authoritative for this line alone.
    if (itemId && productTiers[itemId]) {
      const tier = bestTierFor(productTiers[itemId], qty)
      if (tier) {
        entry.amount = computeAmount(tier, qty, subtotal)
        entry.scope = 'product'
        entry.tier = tier
        entry.poolKey = `product:${itemId}`
      }
      return entry
    }

    // No chart of its own → the org-wide pool.
    if (orgWideTiers.length) {
      poolTiers.org = orgWideTiers
      poolQty.org = (poolQty.org || 0) + qty
      if (!poolLines.org) poolLines.org = []
      poolLines.org.push(i)
      entry.scope = 'org'
      entry.poolKey = 'org'
    }
    return entry
  })

  // Each pool resolves once against its summed qty, then stamps the winning
  // tier onto every line that fed it.
  Object.keys(poolLines).forEach((key) => {
    const tier = bestTierFor(normalizeLadder(poolTiers[key]), poolQty[key])
    poolLines[key].forEach((i) => {
      if (!tier) {
        resolved[i].scope = null
        resolved[i].poolKey = null
        return
      }
      resolved[i].amount = computeAmount(tier, resolved[i].quantity, resolved[i].subtotal)
      resolved[i].tier = tier
    })
  })

  return resolved
}

// Bucket view for the banner: one entry per pool that actually earned
// something — a standalone product line, a product group, or the org-wide
// pool. `amount` and `subtotal` are the pool's totals, so summing the
// buckets gives the same figure as summing the per-line breakdown.
// Returns `[{ scope, tier, amount, subtotal, itemId? }]`.
export function eligibleDiscountBuckets (tiers, lines) {
  const buckets = []
  const byPool = {}

  resolveLines(tiers, lines).forEach((r) => {
    if (!r.poolKey || !(r.amount > 0)) return
    if (!byPool[r.poolKey]) {
      byPool[r.poolKey] = { scope: r.scope, tier: r.tier, amount: 0, subtotal: 0 }
      if (r.scope === 'product') byPool[r.poolKey].itemId = Number(r.item_id || 0)
      buckets.push(byPool[r.poolKey])
    }
    byPool[r.poolKey].amount = Number((byPool[r.poolKey].amount + r.amount).toFixed(2))
    byPool[r.poolKey].subtotal = Number((byPool[r.poolKey].subtotal + r.subtotal).toFixed(2))
  })

  return buckets
}

export function eligibleDiscountTotal (buckets) {
  return Number((buckets || []).reduce((acc, b) => acc + Number(b.amount || 0), 0).toFixed(2))
}

// Sum of the line subtotals that a preset discount actually applied to —
// the "discountable base". A tiered discount only touches the qualifying
// lines, so the meaningful headline rate is `discountAmount / base`, not
// `discountAmount / orderTotal` (which dilutes the rate across un-discounted
// items). Returns 0 when nothing qualified.
export function eligibleDiscountBase (buckets) {
  return Number((buckets || []).reduce((acc, b) => acc + Number(b.subtotal || 0), 0).toFixed(2))
}

// Per-LINE resolution of the same rules, so callers can annotate a product
// list with the discount each line actually earned:
// `[{ item_id, scope, tier, amount, percent }]`, in input order. `percent` is
// the line's own discount as a share of its subtotal, which is what makes a
// mixed-discount cart (e.g. one line at 35%, another at 10%) legible.
export function lineDiscountBreakdown (tiers, lines) {
  return resolveLines(tiers, lines).map((r) => ({
    item_id: r.item_id,
    scope: r.amount > 0 ? r.scope : null,
    tier: r.amount > 0 ? r.tier : null,
    amount: r.amount,
    percent: r.subtotal > 0 ? Number(((r.amount / r.subtotal) * 100).toFixed(2)) : 0
  }))
}

// Human-readable label for the banner. Single bucket → that tier's own
// label; multiple → generic count copy.
export function eligibleDiscountLabel (buckets) {
  const list = buckets || []
  if (!list.length) return ''
  const uniqueIds = new Set(list.map((b) => b.tier.id || `${b.tier.item_id || 0}-${b.tier.min_quantity}`))
  if (uniqueIds.size > 1) {
    return `${list.length} tier${list.length === 1 ? '' : 's'} apply`
  }
  const tier = list[0].tier
  if (tier.label) return tier.label
  if (tier.mode === 'flat_per_unit') return `${tier.min_quantity}+ units · ₹${tier.value}/unit off`
  return `${tier.min_quantity}+ units · ${tier.value}% off`
}
